/*!
 * ScrambleType — typewriter + glyph-scramble text engine
 *
 * This file only defines the engine. It does NOT run itself against
 * any selectors — wire that up in your own init file, e.g.:
 *
 *   ScrambleType.init(".scramble-type");
 *   ScrambleType.init(".slideshow__slide-content .h1", { speed: 20, align: "center-mobile" });
 *
 * Cycle per element: scramble-reveal each character left to right
 * (typewriter cadence) -> hold full text with a blinking cursor for
 * the pause duration -> clear -> repeat indefinitely.
 *
 * Each target element needs the companion CSS (cursor, alignment,
 * blink keyframes) — see scramble-type.css / the .scramble-type rules.
 */

/* ===================================================================
    Typewriter + glyph-scramble text effect
    Usage: <span class="scramble-type" data-text="YOUR TEXT HERE"></span>
    Requires the companion script at the bottom of this file (or copy
    the ScrambleType class into your own JS bundle).
    Optional per-element overrides via data attributes:
      data-speed     ms between characters while typing (default 15)
      data-pause     ms to hold with blinking cursor     (default 5000)
    Alignment modifiers (add alongside .scramble-type):
      .st-left            left-aligned (default)
      .st-center          center-aligned
      .st-center-mobile   centered below 768px, left-aligned at 768px+
    Typography modifier (opt in, doesn't override elements targeted
    via selector that should keep their own font/color):
      .st-mono       monospace look used by the demo heading below
    Elements can also be targeted purely via CSS selector without any
    class in the markup — see SCRAMBLE_SELECTORS in the script below.
    The script adds the "scramble-type" class automatically so this
    structural CSS still applies; it deliberately does NOT add
    "st-mono", so an existing heading keeps its own font/color unless
    you opt in.
    
=================================================================== */


(function (window, document) {
  "use strict";

  const GLYPHS = "!<>-_\\/[]{}—=+*^?#$%&ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

  const DEFAULTS = {
    speed: 25,          // ms between characters while typing
    pause: 5000,         // ms to hold the full text with a blinking cursor
    scrambleFrames: 2,   // scramble frames shown before a character locks in
    scrambleSpeed: 12,   // ms per scramble frame
  };

  class ScrambleType {
    constructor(el, options = {}) {
      this.el = el;
      this.text = options.text || el.getAttribute("data-text") || el.textContent.trim();

      this.speed = options.speed ?? (parseInt(el.getAttribute("data-speed"), 10) || DEFAULTS.speed);
      this.pause = options.pause ?? (parseInt(el.getAttribute("data-pause"), 10) || DEFAULTS.pause);
      this.scrambleFrames = options.scrambleFrames ?? DEFAULTS.scrambleFrames;
      this.scrambleSpeed = options.scrambleSpeed ?? DEFAULTS.scrambleSpeed;

      el.classList.add("scramble-type"); // ensures the companion CSS applies

      // Optional alignment: "left" (default) | "center" | "center-mobile"
      if (options.align) {
        el.classList.add(`st-${options.align}`);
      }

      el.textContent = "";

      this.cursor = document.createElement("span");
      this.cursor.className = "st-cursor";
      el.appendChild(this.cursor);

      this.currentWord = null; // active "no break inside this word" wrapper
      this._stopped = false;
      this.run();
    }

    wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }

    randomGlyph() {
      return GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
    }

    async typeChar(char) {
      const span = document.createElement("span");

      if (char === " ") {
        // Spaces are the only valid line-break points; insert directly
        // and close out whatever word wrapper was open.
        this.currentWord = null;
        this.el.insertBefore(span, this.cursor);
      } else {
        // Group consecutive non-space characters in a nowrap wrapper so
        // the browser can never break a line in the middle of a word.
        if (!this.currentWord) {
          this.currentWord = document.createElement("span");
          this.currentWord.className = "st-word";
          this.el.insertBefore(this.currentWord, this.cursor);
        }
        this.currentWord.appendChild(span);
      }

      for (let i = 0; i < this.scrambleFrames; i++) {
        span.textContent = char === " " ? " " : this.randomGlyph();
        await this.wait(this.scrambleSpeed);
      }
      span.textContent = char;
    }

    async clearAll() {
      Array.from(this.el.childNodes).forEach((node) => {
        if (node !== this.cursor) node.remove();
      });
      this.currentWord = null;
    }

    async run() {
      while (!this._stopped) {
        this.el.classList.remove("st-idle");

        for (const char of this.text) {
          await this.typeChar(char);
          await this.wait(this.speed);
        }

        this.el.classList.add("st-idle");
        await this.wait(this.pause);

        await this.clearAll();
      }
    }

    /** Stop the loop after the current cycle finishes. */
    destroy() {
      this._stopped = true;
    }
  }

  /**
   * Apply the effect to every element matching a selector (or a single
   * element / NodeList / array of elements). Returns the created
   * ScrambleType instances so you can call .destroy() later if needed.
   *
   *   ScrambleType.init(".scramble-type");
   *   ScrambleType.init(".slideshow__slide-content .h1", { speed: 20, pause: 4000 });
   *   ScrambleType.init(document.querySelector("#hero-title"));
   */
  function init(target, options = {}) {
    let elements;
    if (typeof target === "string") {
      elements = document.querySelectorAll(target);
    } else if (target instanceof Element) {
      elements = [target];
    } else {
      elements = target; // NodeList / array-like
    }

    const instances = [];
    elements.forEach((el) => instances.push(new ScrambleType(el, options)));
    return instances;
  }

  ScrambleType.init = init;
  window.ScrambleType = ScrambleType;
})(window, document);
